import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { submitClipGeometryFile } from "data/mutations-common";
import { useS3FileUploadDropper } from "pages/geoscape-admin/custom-datasets/custom-hook/customHooks";
import { useIdentity } from "contexts/identity-context";
import { resolveIdentityId } from "utils/identity";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { zoomToFeatureCollection } from "pages/geoscape-data/explorer/utils";
import type { Feature, GeoJsonProperties, Geometry } from "geojson";
import type { MapRef } from "react-map-gl/maplibre";
import { BoundaryDraw } from "../draw/boundary-draw";

type FeatureType = Feature<Geometry, GeoJsonProperties>;

export type UploadState = "ready" | "uploading" | "processing" | "complete";

const uploadBucket =
  import.meta.env.VITE_ENVIRONMENT === "production"
    ? "prod-hub-clip-storage"
    : "preprod-hub-clip-storage";

interface UseGeometryUploadOptions {
  draw: BoundaryDraw;
  addOrUpdateFeatures: (feat: FeatureType | FeatureType[]) => void;
  mapRef?: React.RefObject<MapRef | null> | React.MutableRefObject<MapRef | undefined>;
  zoomPadding?: number;
}

export function useGeometryUpload({
  draw,
  addOrUpdateFeatures,
  mapRef,
  zoomPadding = 40,
}: UseGeometryUploadOptions) {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const { enqueueMutationFailed } = useCustomSnackbars();

  const [uploadState, setUploadState] = useState<UploadState>("ready");
  const [featureExtractCount, setFeatureExtractCount] = useState(0);
  const [warnings, setWarnings] = useState<string[]>([]);

  const {
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    removeFile,
    getRootProps,
    getInputProps,
    uploadFiles,
  } = useS3FileUploadDropper({
    maxSize: 10 * 1024 * 1024,
    maxFiles: 1,
    prefixFileName: `${identityId}/raw_input/`,
    acceptExtension: {
      "application/zip": [".zip"],
      "application/x-zip-compressed": [".zip"],
    },
  });

  useEffect(() => {
    if (files.length > 0 && !isUploading) {
      uploadFiles(uploadBucket);
      setUploadState("uploading");
    }
  }, [files]);

  const submitFileMutation = useMutation({
    mutationFn: (s3Key: string) => submitClipGeometryFile(s3Key),
    onSuccess: (fc: GeoJSON.FeatureCollection & { messages?: string[] }) => {
      setUploadState("complete");
      setWarnings(fc.messages ?? []);
      setFeatureExtractCount(fc.features.length);
      addOrUpdateFeatures(fc.features as FeatureType[]);
      fc.features.forEach((f) => draw.add(f));
      if (mapRef) {
        zoomToFeatureCollection(mapRef as React.Ref<MapRef>, fc as any, zoomPadding);
      }
    },
    onError: (error: Error) => {
      setUploadState("complete");
      enqueueMutationFailed(error.toString());
    },
  });

  useEffect(() => {
    if (haveUploaded === true) {
      setUploadState("processing");
      const uploadedFileKey = `${identityId}/raw_input/${files[0].name}`;
      submitFileMutation.mutate(uploadedFileKey);
      files.forEach((x) => removeFile(x.name));
    }
  }, [haveUploaded]);

  const resetUpload = () => setUploadState("ready");

  const cancelUpload = (path: string) => {
    removeFile(path);
    setUploadState("ready");
  };

  return {
    uploadState,
    featureExtractCount,
    warnings,
    uploadProgress,
    isUploading,
    haveUploaded,
    files,
    getRootProps,
    getInputProps,
    resetUpload,
    cancelUpload,
  };
}
